import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { emptyTableControls, TableAction, TableControlsState, TableMenuType } from "./types";
import { createEmptyTableCell, focusTableCell, getColumnCount } from "./noteTableUtils";

interface UseNoteTableControlsProps {
    editorRef: RefObject<HTMLDivElement>;
    onContentChange: () => void;
    isOpen: boolean;
}

const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

export const useNoteTableControls = ({
    editorRef,
    onContentChange,
    isOpen,
}: UseNoteTableControlsProps) => {
    const [tableControls, setTableControls] =
        useState<TableControlsState>(emptyTableControls);
    const contentAreaRef = useRef<HTMLDivElement>(null);
    const activeTableCellRef = useRef<HTMLTableCellElement | null>(null);

    const updateTableControls = useCallback((menu?: TableMenuType) => {
        const editor = editorRef.current;
        const contentArea = contentAreaRef.current;
        const selection = window.getSelection();

        if (!editor || !contentArea || !selection || selection.rangeCount === 0) {
            activeTableCellRef.current = null;
            setTableControls(emptyTableControls);
            return;
        }

        const range = selection.getRangeAt(0);
        if (!editor.contains(range.commonAncestorContainer)) {
            activeTableCellRef.current = null;
            setTableControls(emptyTableControls);
            return;
        }

        const node = range.commonAncestorContainer;
        const element = node instanceof HTMLElement ? node : node.parentElement;
        const cell = element?.closest("td, th") as HTMLTableCellElement | null;

        if (!cell || !editor.contains(cell)) {
            activeTableCellRef.current = null;
            setTableControls(emptyTableControls);
            return;
        }

        const row = cell.parentElement as HTMLTableRowElement | null;
        const tableRect = cell.closest("table")?.getBoundingClientRect();
        if (!row) return;

        const contentAreaRect = contentArea.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const cellRect = cell.getBoundingClientRect();
        const cellIsVisible =
            cellRect.bottom >= editorRect.top &&
            cellRect.top <= editorRect.bottom &&
            cellRect.right >= editorRect.left &&
            cellRect.left <= editorRect.right;

        if (!cellIsVisible) {
            setTableControls(emptyTableControls);
            return;
        }

        const editorTop = editorRect.top - contentAreaRect.top;
        const editorBottom = editorRect.bottom - contentAreaRect.top;
        const rowTop = rowRect.top - contentAreaRect.top + rowRect.height / 2;
        const columnTop = (tableRect?.top || cellRect.top) - contentAreaRect.top - 8;

        activeTableCellRef.current = cell;
        setTableControls((current) => ({
            visible: true,
            rowTop: clamp(rowTop, editorTop + 12, editorBottom - 12),
            rowLeft: (tableRect?.right || rowRect.right) - contentAreaRect.left - 2,
            columnTop: clamp(columnTop, editorTop + 12, editorBottom - 12),
            columnLeft: cellRect.left - contentAreaRect.left + cellRect.width / 2,
            menu: menu === undefined ? current.menu : menu,
        }));
    }, [editorRef]);

    useEffect(() => {
        if (!isOpen) return;

        const handleSelectionChange = () => updateTableControls();
        document.addEventListener("selectionchange", handleSelectionChange);
        return () => document.removeEventListener("selectionchange", handleSelectionChange);
    }, [isOpen, updateTableControls]);

    const runTableAction = (action: TableAction) => {
        const editor = editorRef.current;
        const cell = activeTableCellRef.current;
        const row = cell?.parentElement as HTMLTableRowElement | null;
        const table = cell?.closest("table") as HTMLTableElement | null;
        if (!editor || !cell || !row || !table) return;

        editor.focus();

        if (action === "addRowAbove" || action === "addRowBelow") {
            const newRow = document.createElement("tr");
            Array.from({ length: getColumnCount(table) }).forEach(() => {
                newRow.appendChild(createEmptyTableCell());
            });

            row.parentElement?.insertBefore(
                newRow,
                action === "addRowAbove" ? row : row.nextSibling
            );
            focusTableCell(newRow.cells[Math.min(cell.cellIndex, newRow.cells.length - 1)]);
        }

        if (action === "deleteRow") {
            if (table.rows.length <= 1) {
                table.remove();
                activeTableCellRef.current = null;
                setTableControls(emptyTableControls);
            } else {
                const focusTarget =
                    row.nextElementSibling?.querySelector("td, th") ||
                    row.previousElementSibling?.querySelector("td, th");
                row.remove();
                if (focusTarget instanceof HTMLTableCellElement) focusTableCell(focusTarget);
            }
        }

        if (action === "addColumnBefore" || action === "addColumnAfter") {
            let focusTarget: HTMLTableCellElement | null = null;
            const columnIndex = action === "addColumnBefore" ? cell.cellIndex : cell.cellIndex + 1;

            Array.from(table.rows).forEach((tableRow) => {
                const newCell = tableRow.insertCell(Math.min(columnIndex, tableRow.cells.length));
                newCell.innerHTML = "<br>";
                if (tableRow === row) focusTarget = newCell;
            });

            if (focusTarget) focusTableCell(focusTarget);
        }

        if (action === "deleteColumn") {
            const columnIndex = cell.cellIndex;

            if (getColumnCount(table) <= 1) {
                table.remove();
                activeTableCellRef.current = null;
                setTableControls(emptyTableControls);
            } else {
                Array.from(table.rows).forEach((tableRow) => {
                    if (tableRow.cells[columnIndex]) tableRow.deleteCell(columnIndex);
                });

                const nextCell =
                    row.cells[Math.min(columnIndex, row.cells.length - 1)] ||
                    table.rows[0]?.cells[0];
                if (nextCell) focusTableCell(nextCell);
            }
        }

        onContentChange();
        requestAnimationFrame(() => updateTableControls(null));
    };

    return {
        contentAreaRef,
        tableControls,
        updateTableControls,
        runTableAction,
    };
};
